import { Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import JsonLd from '@/components/feature/JsonLd';
import { categories, faqs } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

const BASE_URL = 'https://mitao-video.example.com';

export default function Home() {
  const featuredVideos = videos.filter((v) => v.featured).slice(0, 5);
  const trendingVideos = videos.filter((v) => v.trending).slice(0, 6);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '蜜桃视频在线',
    url: BASE_URL,
    description: '蜜桃视频在线是您最佳的高清视频在线观看平台，海量精选视频内容免费畅享，无需下载即可流畅播放，支持多设备访问。',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${BASE_URL}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蜜桃视频在线',
    url: BASE_URL,
    description: '高清视频免费在线观看平台',
    sameAs: [BASE_URL],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '蜜桃视频在线 - 首页',
    url: BASE_URL,
    isPartOf: { '@id': `${BASE_URL}/#website` },
    about: { '@id': `${BASE_URL}/#organization` },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <JsonLd data={{ ...websiteSchema, '@id': `${BASE_URL}/#website` }} />
      <JsonLd data={{ ...organizationSchema, '@id': `${BASE_URL}/#organization` }} />
      <JsonLd data={webPageSchema} />
      <JsonLd data={faqSchema} />
      <SEOMeta
        title="高清视频免费在线观看平台"
        description="蜜桃视频在线是您最佳的高清视频在线观看平台，海量精选视频内容免费畅享，无需下载即可流畅播放，支持多设备访问，随时随地在线观看最新热门视频内容。"
        keywords="蜜桃视频在线,蜜桃视频在线观看,蜜桃视频免费播放,蜜桃高清视频,蜜桃视频在线平台,免费在线视频,蜜桃视频最新内容"
        canonicalUrl={BASE_URL}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20cinematic%20abstract%20composition%20with%20soft%20peach%20and%20amber%20gradients%2C%20flowing%20organic%20shapes%2C%20ethereal%20light%20rays%2C%20dreamy%20atmospheric%20mood%2C%20clean%20minimal%20aesthetic%2C%20elegant%20soft%20textures%2C%20no%20text&width=1600&height=900&seq=mitao-hero-bg&orientation=landscape"
              alt="蜜桃视频在线 - 高清视频平台"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
              <i className="ri-sparkling-line text-accent-300"></i>
              <span className="text-sm text-background-50/90">每日更新 · 高清画质 · 免费观看</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 font-heading leading-tight mb-5">
              高清视频，随心畅享
            </h1>
            <p className="text-base md:text-lg text-background-50/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              海量精选视频内容免费在线观看，无需下载，多设备流畅播放，随时随地发现精彩世界
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/hot"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-fire-line mr-2"></i>
                立即观看
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/30 text-background-50 font-medium text-sm hover:bg-background-50/25 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>

            <div className="mt-10 flex items-center justify-center gap-8 text-background-50/70">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">10万+</p>
                <p className="text-xs mt-1">精选视频</p>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">500万+</p>
                <p className="text-xs mt-1">日活用户</p>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">1080P</p>
                <p className="text-xs mt-1">高清画质</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                探索精彩分类
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                涵盖电影、电视剧、综艺、动漫等多个分类，总有一款适合你
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-5 md:p-6 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 text-center cursor-pointer"
                >
                  <div className="w-11 h-11 md:w-12 md:h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Videos */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-600">编辑精心挑选的优质视频内容</p>
              </div>
              <Link
                to="/category/hot"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-background-50 text-xs">
                      {video.duration}
                    </div>
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-primary-500 text-background-50 text-xs font-medium">
                      精选
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                      {video.description}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {video.views}
                      </span>
                      <span>{video.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Trending Videos */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  热门排行
                </h2>
                <p className="text-sm text-foreground-600">大家都在看的热门视频</p>
              </div>
              <Link
                to="/category/hot"
                className="hidden sm:flex items-center gap-1 text-sm text-accent-500 hover:text-accent-600 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {trendingVideos.map((video, index) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group flex gap-4 bg-background-100 rounded-xl p-4 border border-background-200/70 hover:border-accent-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-accent-600 transition-colors line-clamp-2 mb-1.5">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-1 mb-2">
                      {video.description}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-fire-line text-accent-500"></i>
                        {video.views}
                      </span>
                      <span>{video.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                为什么选择蜜桃视频在线
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                我们致力于为用户提供最优质的高清视频在线观看体验
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '完全免费',
                  desc: '所有视频内容免费观看，无需付费订阅，畅享高清画质体验',
                },
                {
                  icon: 'ri-device-line',
                  title: '多设备支持',
                  desc: '电脑、平板、手机全设备适配，随时随地在线观看视频内容',
                },
                {
                  icon: 'ri-speed-up-line',
                  title: '流畅播放',
                  desc: '智能CDN加速技术，自动适配网络环境，确保流畅不卡顿',
                },
                {
                  icon: 'ri-refresh-line',
                  title: '每日更新',
                  desc: '海量视频资源每日持续更新，第一时间获取最新精彩内容',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">
                关于蜜桃视频在线的常见问题解答
              </p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h3 className="text-sm font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </h3>
                    <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 group-open:bg-primary-100 group-open:text-primary-600 transition-colors">
                      <i className="ri-add-line group-open:ri-subtract-line text-sm transition-all"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=soft%20warm%20abstract%20gradient%20background%20with%20flowing%20peach%20and%20amber%20tones%2C%20dreamy%20ethereal%20atmosphere%2C%20smooth%20gradient%20transitions%2C%20minimalist%20elegant%20design%2C%20no%20sharp%20edges&width=1600&height=600&seq=mitao-cta-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/85 to-accent-600/85"></div>
          <div className="relative z-10 max-w-2xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 font-heading mb-4">
              准备好开始观影了吗？
            </h2>
            <p className="text-sm md:text-base text-background-50/85 mb-8 leading-relaxed">
              海量高清视频免费观看，无需注册，无需下载，立即开始您的观影之旅
            </p>
            <Link
              to="/category/hot"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-primary-600 font-medium text-sm hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-fill text-lg"></i>
              立即免费观看
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}